/**
 * Groups a desktop transcript's partial and final events into ordered turns.
 *
 * The Recall Desktop SDK usually sends an all-zero transcript.id placeholder on
 * desktop. When a real id is available, we use it as the utterance key; otherwise
 * the useful signals are a speaker grouping key and the first word's start time,
 * which stays at the same audio position across an utterance's partials.
 */

const SAME_UTTERANCE_START_TOLERANCE_MS = 50;
const PLACEHOLDER_UTTERANCE_KEYS = new Set(["0", "00000000-0000-0000-0000-000000000000"]);

export interface TurnGroupingEvent {
  channel: 0 | 1;
  speakerGroupingKey: string | null;
  utteranceKey: string | null;
  firstWordStartMs: number | null;
  timestampMs: number;
  isFinal: boolean;
}

export type TurnAssignment = { kind: "assigned"; turnOrder: number } | { kind: "dropped" };

type UtteranceRelation = "same" | "different" | "unknown";

interface ActiveTurn {
  turnOrder: number;
  utteranceKey: string | null;
  firstWordStartMs: number | null;
  timestampMs: number;
}

function channelKey(channel: 0 | 1): string {
  return `channel:${channel}`;
}

function normalizeSpeakerGroupingKey(speakerGroupingKey: string | null): string | null {
  const key = speakerGroupingKey?.trim();
  return key ? key : null;
}

function normalizeUtteranceKey(utteranceKey: string | null): string | null {
  const key = utteranceKey?.trim().toLocaleLowerCase();
  if (!key || PLACEHOLDER_UTTERANCE_KEYS.has(key)) {
    return null;
  }

  return key;
}

export class TranscriptTurnGrouper {
  private nextTurnOrder = 0;
  private readonly activeTurnsBySpeaker = new Map<string, ActiveTurn[]>();
  private readonly finalWatermarksBySpeaker = new Map<string, number>();

  reset(): void {
    this.nextTurnOrder = 0;
    this.activeTurnsBySpeaker.clear();
    this.finalWatermarksBySpeaker.clear();
  }

  assign(event: TurnGroupingEvent): TurnAssignment {
    return event.isFinal ? this.assignFinal(event) : this.assignPartial(event);
  }

  private assignPartial(event: TurnGroupingEvent): TurnAssignment {
    const speakerKey = this.resolveSpeakerKey(event);
    const activeTurn = this.findActiveTurn({ speakerKey, event });

    if (activeTurn !== null) {
      this.rememberActiveTurn({ speakerKey, event, turnOrder: activeTurn.turn.turnOrder });
      return { kind: "assigned", turnOrder: activeTurn.turn.turnOrder };
    }

    if (this.isStalePartial({ event, speakerKey })) {
      return { kind: "dropped" };
    }

    const turnOrder = this.allocateTurnOrder();
    this.rememberActiveTurn({ speakerKey, event, turnOrder });
    return { kind: "assigned", turnOrder };
  }

  private assignFinal(event: TurnGroupingEvent): TurnAssignment {
    const speakerKey = this.resolveSpeakerKey(event);
    this.advanceWatermark({ speakerKey, timestampMs: event.timestampMs });
    const fallbackChannelKey = channelKey(event.channel);
    if (speakerKey !== fallbackChannelKey) {
      this.advanceWatermark({ speakerKey: fallbackChannelKey, timestampMs: event.timestampMs });
    }

    const activeTurn = this.findActiveTurn({ speakerKey, event });
    if (activeTurn !== null) {
      this.forgetActiveTurn({ speakerKey, turnOrder: activeTurn.turn.turnOrder });
      return { kind: "assigned", turnOrder: activeTurn.turn.turnOrder };
    }

    return { kind: "assigned", turnOrder: this.allocateTurnOrder() };
  }

  private resolveSpeakerKey(event: TurnGroupingEvent): string {
    const groupingKey = normalizeSpeakerGroupingKey(event.speakerGroupingKey);
    if (groupingKey === null) {
      return channelKey(event.channel);
    }

    if (!this.hasActiveTurn(groupingKey)) {
      this.adoptChannelTurnIfSameUtterance({ event, speakerKey: groupingKey });
    }

    return groupingKey;
  }

  private adoptChannelTurnIfSameUtterance({
    event,
    speakerKey,
  }: {
    event: TurnGroupingEvent;
    speakerKey: string;
  }): void {
    const fromKey = channelKey(event.channel);
    const channelTurn = this.findActiveTurn({ speakerKey: fromKey, event, relation: "same" });
    if (channelTurn === null) {
      return;
    }

    this.forgetActiveTurn({ speakerKey: fromKey, turnOrder: channelTurn.turn.turnOrder });
    this.rememberActiveTurnObject({ speakerKey, activeTurn: channelTurn.turn });
  }

  private relateToTurn(activeTurn: ActiveTurn, event: TurnGroupingEvent): UtteranceRelation {
    const activeUtteranceKey = activeTurn.utteranceKey;
    const eventUtteranceKey = normalizeUtteranceKey(event.utteranceKey);

    if (activeUtteranceKey !== null && eventUtteranceKey !== null) {
      return activeUtteranceKey === eventUtteranceKey ? "same" : "different";
    }

    const activeStart = activeTurn.firstWordStartMs;
    const eventStart = event.firstWordStartMs;
    if (activeStart === null || eventStart === null) {
      return "unknown";
    }

    const sameAudioPosition =
      Math.abs(activeStart - eventStart) <= SAME_UTTERANCE_START_TOLERANCE_MS;
    return sameAudioPosition ? "same" : "different";
  }

  private isStalePartial({
    event,
    speakerKey,
  }: {
    event: TurnGroupingEvent;
    speakerKey: string;
  }): boolean {
    const ownWatermark = this.finalWatermarksBySpeaker.get(speakerKey);
    const channelKeyForEvent = channelKey(event.channel);
    const channelWatermark =
      speakerKey !== channelKeyForEvent
        ? this.finalWatermarksBySpeaker.get(channelKeyForEvent)
        : undefined;

    const latestWatermark = Math.max(ownWatermark ?? -Infinity, channelWatermark ?? -Infinity);
    return latestWatermark !== -Infinity && event.timestampMs <= latestWatermark;
  }

  private advanceWatermark({
    speakerKey,
    timestampMs,
  }: {
    speakerKey: string;
    timestampMs: number;
  }): void {
    const prior = this.finalWatermarksBySpeaker.get(speakerKey) ?? -Infinity;
    this.finalWatermarksBySpeaker.set(speakerKey, Math.max(prior, timestampMs));
  }

  private rememberActiveTurn({
    speakerKey,
    event,
    turnOrder,
  }: {
    speakerKey: string;
    event: TurnGroupingEvent;
    turnOrder: number;
  }): void {
    this.rememberActiveTurnObject({
      speakerKey,
      activeTurn: {
        turnOrder,
        utteranceKey: normalizeUtteranceKey(event.utteranceKey),
        firstWordStartMs: event.firstWordStartMs,
        timestampMs: event.timestampMs,
      },
    });
  }

  private rememberActiveTurnObject({
    speakerKey,
    activeTurn,
  }: {
    speakerKey: string;
    activeTurn: ActiveTurn;
  }): void {
    const activeTurns = [...(this.activeTurnsBySpeaker.get(speakerKey) ?? [])];
    const existingIndex = activeTurns.findIndex(turn => turn.turnOrder === activeTurn.turnOrder);
    if (existingIndex === -1) {
      activeTurns.push(activeTurn);
    } else {
      activeTurns[existingIndex] = activeTurn;
    }

    this.activeTurnsBySpeaker.set(speakerKey, activeTurns);
  }

  private forgetActiveTurn({
    speakerKey,
    turnOrder,
  }: {
    speakerKey: string;
    turnOrder: number;
  }): void {
    const activeTurns = this.activeTurnsBySpeaker.get(speakerKey);
    if (activeTurns === undefined) return;

    const remainingTurns = activeTurns.filter(turn => turn.turnOrder !== turnOrder);
    if (remainingTurns.length === 0) {
      this.activeTurnsBySpeaker.delete(speakerKey);
      return;
    }

    this.activeTurnsBySpeaker.set(speakerKey, remainingTurns);
  }

  private findActiveTurn({
    speakerKey,
    event,
    relation,
  }: {
    speakerKey: string;
    event: TurnGroupingEvent;
    relation?: UtteranceRelation;
  }): { turn: ActiveTurn; relation: UtteranceRelation } | null {
    let fallback: { turn: ActiveTurn; relation: UtteranceRelation } | null = null;

    for (const activeTurn of this.activeTurnsBySpeaker.get(speakerKey) ?? []) {
      const activeRelation = this.relateToTurn(activeTurn, event);
      if (relation !== undefined && activeRelation !== relation) {
        continue;
      }

      if (activeRelation === "same") {
        return { turn: activeTurn, relation: activeRelation };
      }

      if (activeRelation === "unknown") {
        const fallbackIsBeforeEvent =
          fallback !== null && fallback.turn.timestampMs <= event.timestampMs;
        const activeIsBeforeEvent = activeTurn.timestampMs <= event.timestampMs;

        if (
          fallback === null ||
          (activeIsBeforeEvent && !fallbackIsBeforeEvent) ||
          (activeIsBeforeEvent === fallbackIsBeforeEvent &&
            activeTurn.timestampMs >= fallback.turn.timestampMs)
        ) {
          fallback = { turn: activeTurn, relation: activeRelation };
        }
      }
    }

    return fallback;
  }

  private hasActiveTurn(speakerKey: string): boolean {
    return (this.activeTurnsBySpeaker.get(speakerKey)?.length ?? 0) > 0;
  }

  private allocateTurnOrder(): number {
    const turnOrder = this.nextTurnOrder;
    this.nextTurnOrder += 1;
    return turnOrder;
  }
}
